'use client';

import { useEffect, useState } from 'react';

import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import {
  getFeaturedMostSearchedProcedures,
  getLoadedFeaturedMostSearchedProcedures,
  getMostSearchedGroups,
  getMostSearchedProcedures,
} from '@/services/most-searched/most-searched.service';
import { MostSearchedResultType } from '@/types/most-search-type';
import { getLogger } from '@/logger/logger';

const logger = getLogger('most-searched');

interface UseMostSearchedReturn {
  groups: MostSearchedResultType[];
  isLoading: boolean;
}

interface UseFeaturedMostSearchedReturn {
  procedures: SearchResult[];
  isLoading: boolean;
}

function useMostSearchedFetch<T>(
  fetchItems: () => Promise<T[]>,
  getLoadedItems?: () => T[] | undefined,
): { items: T[]; isLoading: boolean } {
  const [loadedItems] = useState(() => getLoadedItems?.());
  const [items, setItems] = useState<T[]>(loadedItems ?? []);
  const [isLoading, setIsLoading] = useState(!loadedItems);

  useEffect(() => {
    if (loadedItems) {
      return;
    }

    let isActive = true;

    fetchItems()
      .then((fetchedItems) => {
        if (isActive) {
          setItems(fetchedItems);
        }
      })
      .catch((error) => {
        logger.error('Failed to fetch most searched procedures', {
          most_searched: { event: 'fetch_failed' },
          error,
        });
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [fetchItems, loadedItems]);

  return { items, isLoading };
}

export function useMostSearchedGroups(): UseMostSearchedReturn {
  const { items, isLoading } = useMostSearchedFetch(getMostSearchedGroups);

  return { groups: items, isLoading };
}

export function useMostSearchedProcedures(): UseMostSearchedReturn {
  const { items, isLoading } = useMostSearchedFetch(getMostSearchedProcedures);

  return { groups: items, isLoading };
}

export function useFeaturedMostSearchedProcedures(): UseFeaturedMostSearchedReturn {
  const { items, isLoading } = useMostSearchedFetch(
    getFeaturedMostSearchedProcedures,
    getLoadedFeaturedMostSearchedProcedures,
  );

  return { procedures: items, isLoading };
}
